import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Callout, Paragraph, Select, Spinner, Subheading, Switch } from '@treams/treams-ui';

import EmployeesMultiFilter from '../../packages/kakapo-ui-application/pages/employees/EmployeesFilter';

function EvaluationFormSelector() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useEvaluationFormsQuery({ variables: { isArchived: false, first: 1000 } });

	if (!query.data) {
		return <Spinner centered />;
	}

	const options = query.data.evaluationForms.edges.map(({ node }) => ({
		value: node.id,
		label: localize(node.title).text,
	}));

	return (
		<EmployeesMultiFilter
			name="evaluationFormIDs"
			label={t('managerInbox.exports.score.evaluationForm')}
			options={_.sortBy(options, 'label')}
		/>
	);
}

function TalentReviewTemplateSelector() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	if (!query.data) {
		return <Spinner centered />;
	}

	const options = query.data.talentReviewTemplates.edges.map(({ node }) => ({
		value: node.id,
		label: localize(node.title).text,
	}));

	return (
		<EmployeesMultiFilter
			name="talentReviewTemplateIDs"
			label={t('managerInbox.exports.score.talentReviewTemplate')}
			options={_.sortBy(options, 'label')}
		/>
	);
}

export default function DashboardDownloadScoresOptions(props: {
	startDate: Date;
	endDate: Date;
	groupIDs?: string[];
	onFinish: () => void;
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const { addToast } = useToasts();

	const [loading, setLoading] = useState(false);

	const [exportUsers] = graphql.useExportUsersLazyQuery({
		fetchPolicy: 'network-only',
	});

	const [format, setFormat] = useState(graphql.ExportFormat.Excel);
	const [group, setGroup] = useState(false);
	const [includeManagerDrafts, setIncludeManagerDrafts] = useState(false);
	const [archivedUsers, setArchivedUsers] = useState(false);
	const [archivedConversations, setArchivedConversations] = useState(false);

	const formik = useFormik({
		initialValues: {
			evaluationFormIDs: [] as string[],
			talentReviewTemplateIDs: [] as string[],
		},
		onSubmit: async () => {
			// Guard against a second submit while one is in flight: the export
			// can run for minutes on a large workspace and every extra request
			// ties up a backend task.
			if (loading) {
				return;
			}

			setLoading(true);

			let url: string | undefined;

			try {
				const response = await exportUsers({
					variables: {
						input: {
							languageID: language.id,
							conversationTemplateIDs: formik.values.evaluationFormIDs,
							talentReviewTemplateIDs: formik.values.talentReviewTemplateIDs,
							departmentIDs: props.groupIDs,
							startDate: props.startDate,
							endDate: props.endDate,
							status: !archivedUsers ? graphql.UserStatus.Active : graphql.UserStatus.Archived,
							archivedConversations,
							groupByUser: group,
							includeManagerDrafts,
							format,
						},
					},
				});

				// A lazy query resolves with `error` set rather than rejecting.
				url = response.error ? undefined : (response.data?.exportUsers.url ?? undefined);
			} catch {
				url = undefined;
			} finally {
				// Also runs when the request fails (e.g. the load balancer's
				// 120s timeout), so the button never stays stuck on loading.
				setLoading(false);
			}

			if (!url) {
				addToast(t('managerInbox.exports.score.failed'), {
					appearance: 'error',
					autoDismiss: true,
				});
				return;
			}

			const link = document.createElement('a');
			link.href = url;
			link.rel = 'noopener';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			props.onFinish();
		},
	});

	return (
		<Box p={3} pt={0} minWidth={340}>
			<FormikProvider value={formik}>
				<Box my={3}>
					<Subheading mb={-2}>{t('managerInbox.exports.title.templates')}</Subheading>
					<EvaluationFormSelector />
					<TalentReviewTemplateSelector />

					<Subheading mt={3}>{t('managerInbox.exports.title.archive')}</Subheading>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.checkbox.archivedUsers')}</Paragraph>
						</Box>
						<Switch on={archivedUsers} onChange={setArchivedUsers} />
					</Flex>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.checkbox.archivedConversations')}</Paragraph>
						</Box>
						<Switch on={archivedConversations} onChange={setArchivedConversations} />
					</Flex>

					<Subheading mt={3}>{t('managerInbox.exports.title.file')}</Subheading>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.score.group')}</Paragraph>
						</Box>
						<Switch on={group} onChange={setGroup} />
					</Flex>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.checkbox.includeManagerDrafts')}</Paragraph>
						</Box>
						<Switch on={includeManagerDrafts} onChange={setIncludeManagerDrafts} />
					</Flex>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.score.format.label')}</Paragraph>
						</Box>
						<Select
							search={false}
							selectedValue={format}
							onChange={(format) => setFormat(format as graphql.ExportFormat)}
							options={[
								{
									value: graphql.ExportFormat.Excel,
									title: t('managerInbox.exports.score.format.excel'),
								},
								{
									value: graphql.ExportFormat.Csv,
									title: t('managerInbox.exports.score.format.csv'),
								},
							]}
						/>
					</Flex>
					{loading && (
						<Callout color="blue" maxWidth={300} mt={3}>
							{t('managerInbox.exports.score.explanation')}
						</Callout>
					)}
				</Box>
				<Button
					loading={loading}
					type="primary"
					title={t('managerInbox.exports.score.download')}
					onClick={() => formik.submitForm()}
					disabled={
						loading ||
						(!formik.values.evaluationFormIDs.length &&
							!formik.values.talentReviewTemplateIDs.length)
					}
				/>
			</FormikProvider>
		</Box>
	);
}
